import { Injectable, inject } from '@angular/core';
import { Geolocation, PermissionStatus as CapacitorPermissionStatus, Position, PositionOptions } from '@capacitor/geolocation';
import { PermissionState as CapacitorPermissionState } from '@capacitor/core/types';
import { Client, PositionResponse, StoreUserLocationRequestSource, UpdateVideoLocationRequest, UpdateVideoLocationRequestLocation_source } from '@api/client';
import { LocalStorageService } from '@services/local-storage.service';
import { OnlineStateService } from '@services/online-state.service';
import { PlatformService } from '@services/platform.service';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import { DEFAULT_AVATAR_COORDINATE, UPDATE_INTERVAL } from '../avatar.constants';
import { BehaviorSubject, EMPTY, firstValueFrom, from, Observable, Subject, Subscription, timer } from 'rxjs';
import { catchError, debounceTime, exhaustMap, filter, first, tap } from 'rxjs/operators';

export type LocationKind = 'gps' | 'ip' | 'cache' | 'fallback' | 'blocked' | 'offline';

export type LocationBlockReason =
  | 'permission-denied'
  | 'permission-prompt'
  | 'disabled'
  | 'not-supported'
  | 'offline'
  | 'error';

export interface LocationResult {
  kind: LocationKind;
  coords?: { latitude: number; longitude: number };
  source?: StoreUserLocationRequestSource | 'fallback';
  reason?: LocationBlockReason;
}

export type CoordinateUpdateSource = StoreUserLocationRequestSource | 'fallback';

export interface CoordinateSnapshot {
  lat: number;
  lon: number;
  source: CoordinateUpdateSource;
  timestamp: number;
  precisionRank: number;
  isFromCache: boolean;
}

export interface CoordinatePair {
  latitude: number;
  longitude: number;
}

export type GpsPolicy = 'allow' | 'ask' | 'deny';

export interface PermissionStatusNormalized {
  permission: 'granted' | 'prompt' | 'denied' | 'unknown';
  enabled: boolean | null; // null = не удалось определить
  platform: 'device' | 'web';
}

interface CachedCoordinatePayload {
  lat: number;
  lon: number;
  source: StoreUserLocationRequestSource;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class LocationAcquisitionService {
  private readonly client = inject(Client);
  private readonly localStorage = inject(LocalStorageService);
  private readonly platformService = inject(PlatformService);
  private readonly onlineStateService = inject(OnlineStateService);
  private readonly diagnostic = inject(Diagnostic);

  private readonly cacheKey: string = 'avatar:lastCoordinates';
  private readonly cacheTtlMs: number = 24 * 60 * 60 * 1000;
  private readonly cacheEnabled: boolean = true;
  private currentLatValue: number | null = null;
  private currentLonValue: number | null = null;
  private hasValidCoordinatesFlag: boolean = false;
  private coordinatePrecisionRank: number = -1;
  private lastSavedPosition: Position | GeolocationPosition | null = null;
  private coordinateSnapshotSubject: BehaviorSubject<CoordinateSnapshot | null> = new BehaviorSubject<CoordinateSnapshot | null>(null);
  public readonly coordinateSnapshot$ = this.coordinateSnapshotSubject.asObservable();
  private ensureCoordinatesPromise: Promise<void> | null = null;
  private locationWatching: boolean = false;
  private readonly gpsPolicyKey: string = 'avatar:gpsPolicy';
  private readonly gpsPermissionSnapshotKey: string = 'avatar:gpsPermissionSnapshot';
  private readonly geolocationOptions: PositionOptions = {
    maximumAge: UPDATE_INTERVAL.MY_GPS,
    timeout: UPDATE_INTERVAL.MY_GPS,
    enableHighAccuracy: false,
  };
  private readonly precisionRank: Record<LocationKind, number> = {
    gps: 3,
    ip: 2,
    cache: 2,
    fallback: 1,
    blocked: 0,
    offline: 0,
  };
  private lastPrecisionRank: number = -1;
  private improvedCoordinateSubject: Subject<{ lat: number; lon: number; source: StoreUserLocationRequestSource | 'fallback'; precisionRank: number }> = new Subject();
  public readonly improvedCoordinate$ = this.improvedCoordinateSubject.asObservable();
  private videoTrackSub: Subscription | null = null;
  private videoImproveSub: Subscription | null = null;
  private videoIpFallbackSub: Subscription | null = null;
  private currentVideoId: number | null = null;
  private prevVideoLat: number | null = null;
  private prevVideoLon: number | null = null;
  private videoIpFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private videoGpsSent: boolean = false;
  private videoIpSent: boolean = false;
  private videoLocationSending: boolean = false;
  private readonly videoIpFallbackMs: number = 60_000;
  private readonly videoLocationSource = UpdateVideoLocationRequestLocation_source;

  public resetPrecisionTracking(): void {
    this.lastPrecisionRank = -1;
  }

  constructor() {
    this.restoreCachedCoordinates();
  }

  public get currentLat(): number | null {
    return this.currentLatValue;
  }

  public get currentLon(): number | null {
    return this.currentLonValue;
  }

  public get hasValidCoordinates(): boolean {
    return this.hasValidCoordinatesFlag;
  }

  public get currentCoordinatePrecision(): number {
    return this.coordinatePrecisionRank;
  }

  public get isLocationWatching(): boolean {
    return this.locationWatching;
  }

  public setLocationWatching(value: boolean): void {
    this.locationWatching = value;
  }

  public getLastSavedPosition(): Position | GeolocationPosition | null {
    return (this.locationWatching && this.lastSavedPosition) ? this.lastSavedPosition : null;
  }

  public getStoredCoordinates(): CoordinatePair | null {
    const latitude = this.currentLatValue;
    const longitude = this.currentLonValue;

    if (this.hasValidCoordinatesFlag && typeof latitude === 'number' && typeof longitude === 'number') {
      return { latitude, longitude };
    }

    const cachedCoordinate = this.getCachedCoordinate();

    if (cachedCoordinate) {
      return { latitude: cachedCoordinate.latitude, longitude: cachedCoordinate.longitude };
    }

    return null;
  }

  public updateCoordinates(
    latitude: number,
    longitude: number,
    source: CoordinateUpdateSource,
    options: { persist?: boolean; isFromCache?: boolean; forceDowngrade?: boolean } = {}
  ): void {
    this.applyCoordinate(latitude, longitude, source, options);
  }

  public updateFromPosition(
    position: Position | GeolocationPosition,
    source: StoreUserLocationRequestSource = StoreUserLocationRequestSource.Gps
  ): CoordinatePair | null {
    if (!position?.coords) return null;
    const latitude: number = position.coords.latitude;
    const longitude: number = position.coords.longitude;
    this.lastSavedPosition = position;
    this.applyCoordinate(latitude, longitude, source);
    return { latitude, longitude };
  }

  public async ensureInitialCoordinates(): Promise<void> {
    if (this.ensureCoordinatesPromise) {
      await this.ensureCoordinatesPromise;
      return;
    }

    this.ensureCoordinatesPromise = (async () => {
      await this.refreshPermissionSnapshot().catch(() => {});
      await this.acquireBackground();
    })();

    try {
      await this.ensureCoordinatesPromise;
    } finally {
      this.ensureCoordinatesPromise = null;
    }
  }

  public async getFirstCoordinate(): Promise<Position | GeolocationPosition> {
    await this.acquireExplicit();
    const coords = this.getStoredCoordinates();
    if (!coords) {
      throw new Error('Coordinates are not available');
    }
    return this.buildPseudoPosition(coords.latitude, coords.longitude);
  }

  public async getCoordinateForCenter(): Promise<Position | GeolocationPosition> {
    await this.acquireForCenter();
    const coords = this.getStoredCoordinates();
    if (!coords) {
      throw new Error('Coordinates are not available');
    }
    return this.buildPseudoPosition(coords.latitude, coords.longitude);
  }

  //#region Policy helpers
  public get gpsPolicy(): GpsPolicy {
    const saved: string | null = this.localStorage.getItem(this.gpsPolicyKey);
    if (saved === 'allow' || saved === 'ask' || saved === 'deny') return saved;
    return 'allow';
  }

  public setGpsPolicy(policy: GpsPolicy): void {
    this.localStorage.setItem(this.gpsPolicyKey, policy);
  }
  //#endregion Policy helpers

  //#region Permission status
  public async getPermissionStatus(): Promise<PermissionStatusNormalized> {
    // Device
    if (this.platformService.isDevice) {
      try {
        const permissions: CapacitorPermissionStatus = await Geolocation.checkPermissions();
        const permission: 'granted' | 'prompt' | 'denied' | 'unknown' =
          (permissions.location === 'granted' || permissions.coarseLocation === 'granted') ? 'granted'
            : ((permissions.location === 'prompt' || permissions.location === 'prompt-with-rationale' || permissions.coarseLocation === 'prompt' || permissions.coarseLocation === 'prompt-with-rationale') ? 'prompt'
              : (permissions.location === 'denied' && permissions.coarseLocation === 'denied') ? 'denied'
                : 'unknown');

        let enabled: boolean | null = null;
        try {
          enabled = await this.diagnostic.isLocationEnabled();
        } catch (_err) {
          enabled = null;
        }

        return { permission, enabled, platform: 'device' };
      } catch (_err) {
        return { permission: 'unknown', enabled: null, platform: 'device' };
      }
    }

    // Browser
    if (typeof navigator !== 'undefined' && 'permissions' in navigator && (navigator as any).permissions?.query) {
      try {
        const status: PermissionStatus = await (navigator as Navigator & { permissions: Permissions }).permissions.query({ name: 'geolocation' });
        const permission: 'granted' | 'prompt' | 'denied' =
          status.state === 'granted' ? 'granted' :
            status.state === 'prompt' ? 'prompt' : 'denied';
        return { permission, enabled: null, platform: 'web' };
      } catch (_err) {
        return { permission: 'unknown', enabled: null, platform: 'web' };
      }
    }

    return { permission: 'unknown', enabled: null, platform: 'web' };
  }
  //#endregion Permission status

  public async acquire(
    mode: 'interactive' | 'background',
    options: { allowPrompt?: boolean; policy?: GpsPolicy; forcePrompt?: boolean } = {}
  ): Promise<LocationResult> {
    const policy: GpsPolicy = options.policy ?? 'allow';
    const allowPrompt: boolean = options.allowPrompt ?? (mode === 'interactive' && policy === 'ask');
    const forcePrompt: boolean = options.forcePrompt ?? false;

    if (policy === 'deny') {
      return this.finalizeAcquireResult({ kind: 'blocked', reason: 'permission-denied' });
    }

    // 1. GPS
    const gpsResult = await this.tryGps(mode, allowPrompt, forcePrompt);
    if (gpsResult.kind === 'gps') {
      return this.finalizeAcquireResult(gpsResult);
    }
    if (gpsResult.kind === 'blocked' && mode === 'background') {
      // не пытаемся лезть дальше в фоне, но дадим шанс IP/фолбэк
    }

    // 2. cache (если включен)
    const cached = this.getCachedCoordinate();
    if (cached) {
      return this.finalizeAcquireResult({ kind: 'cache', coords: cached, source: cached.source });
    }

    // 3. IP (если есть сеть)
    const ipResult = await this.tryIp();
    if (ipResult) {
      return this.finalizeAcquireResult(ipResult);
    }

    // 4. fallback
    const fallback: LocationResult = {
      kind: 'fallback',
      coords: { latitude: DEFAULT_AVATAR_COORDINATE.lat, longitude: DEFAULT_AVATAR_COORDINATE.lon },
      source: 'fallback'
    };
    return this.finalizeAcquireResult(fallback);
  }

  private finalizeAcquireResult(result: LocationResult): LocationResult {
    this.registerImprovement(result);
    this.applyLocationResult(result);
    return result;
  }

  //#region GPS
  private async tryGps(mode: 'interactive' | 'background', allowPrompt: boolean, forcePrompt: boolean): Promise<LocationResult> {
    if (!this.platformService.isDevice && !(typeof navigator !== 'undefined' && 'geolocation' in navigator)) {
      return { kind: 'blocked', reason: 'not-supported' };
    }

    if (this.platformService.isDevice) {
      return this.tryGpsOnDevice(mode, allowPrompt, forcePrompt);
    }

    return this.tryGpsInBrowser(mode, allowPrompt);
  }

  private async tryGpsOnDevice(mode: 'interactive' | 'background', allowPrompt: boolean, forcePrompt: boolean): Promise<LocationResult> {
    try {
      const enabled = await this.isLocationEnabledSafe();
      if (!enabled) {
        console.log('[Geo] Device GPS disabled');
        return { kind: 'blocked', reason: 'disabled' };
      }

      // Всегда дергаем requestPermissions для интерактивных действий, если нам можно показывать системный диалог.
      if (mode === 'interactive' && allowPrompt) {
        console.log('[Geo] requestPermissions (interactive)');
        const req = await Geolocation.requestPermissions();
        const granted = req.location === 'granted' || req.coarseLocation === 'granted';
        await this.refreshPermissionSnapshot().catch(() => {});
        if (!granted) {
          console.log('[Geo] requestPermissions denied');
          return { kind: 'blocked', reason: 'permission-denied' };
        }
      } else {
        const permissions: CapacitorPermissionStatus = await Geolocation.checkPermissions();
        const location: CapacitorPermissionState = permissions.location;
        const coarseLocation: CapacitorPermissionState = permissions.coarseLocation;
        const isGranted = location === 'granted' || coarseLocation === 'granted';
        await this.refreshPermissionSnapshot().catch(() => {});

        if (!isGranted) {
          console.log('[Geo] checkPermissions denied (background/no-prompt)');
          return { kind: 'blocked', reason: 'permission-denied' };
        }
      }

      const position: Position = await Geolocation.getCurrentPosition(this.geolocationOptions);
      return {
        kind: 'gps',
        coords: { latitude: position.coords.latitude, longitude: position.coords.longitude },
        source: StoreUserLocationRequestSource.Gps
      };
    } catch (error) {
      console.warn('[Geo] tryGpsOnDevice error', error);
      return { kind: 'blocked', reason: 'error' };
    }
  }

  private async tryGpsInBrowser(mode: 'interactive' | 'background', allowPrompt: boolean): Promise<LocationResult> {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return { kind: 'blocked', reason: 'not-supported' };
    }

    const hasPermissionsApi = 'permissions' in navigator;

    if (hasPermissionsApi) {
      try {
        const status: PermissionStatus = await (navigator as Navigator & { permissions: Permissions }).permissions.query({ name: 'geolocation' });
        if (status.state !== 'granted') {
          if (!allowPrompt) {
            return { kind: 'blocked', reason: status.state === 'prompt' ? 'permission-prompt' : 'permission-denied' };
          }
        }
      } catch (_err) {
        // ignore and continue to attempt
      }
    }

    if (!allowPrompt && !hasPermissionsApi) {
      return { kind: 'blocked', reason: 'permission-denied' };
    }

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        const timeoutId = setTimeout(() => reject(new Error('timeout')), this.geolocationOptions.timeout);
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            clearTimeout(timeoutId);
            resolve(pos);
          },
          (err) => {
            clearTimeout(timeoutId);
            reject(err);
          },
          this.geolocationOptions
        );
      });

      return {
        kind: 'gps',
        coords: { latitude: position.coords.latitude, longitude: position.coords.longitude },
        source: StoreUserLocationRequestSource.Gps
      };
    } catch (error: any) {
      const reason: LocationBlockReason = (error?.code === error.PERMISSION_DENIED)
        ? 'permission-denied'
        : 'error';
      return { kind: 'blocked', reason };
    }
  }

  private async isLocationEnabledSafe(): Promise<boolean> {
    if (typeof window === 'undefined' || !this.platformService.isDevice) return true;
    try {
      return await this.diagnostic.isLocationEnabled();
    } catch (_err) {
      return false;
    }
  }

  public async isSystemLocationEnabled(): Promise<boolean> {
    return this.isLocationEnabledSafe();
  }

  public async hasActiveLocationPermission(): Promise<boolean> {
    if (this.platformService.isDevice) {
      try {
        const permissions: CapacitorPermissionStatus = await Geolocation.checkPermissions();
        const { location, coarseLocation } = permissions;
        return location === 'granted' || coarseLocation === 'granted';
      } catch (_err) {
        console.warn('[Geo] Unable to check device permissions', _err);
        return false;
      }
    }

    if (typeof navigator !== 'undefined' && 'permissions' in navigator) {
      try {
        const permissionsApi: Permissions = (navigator as Navigator & { permissions: Permissions }).permissions;
        const status: PermissionStatus = await permissionsApi.query({ name: 'geolocation' });
        return status.state === 'granted';
      } catch (_err) {
        console.warn('[Geo] Unable to check browser permissions', _err);
        return false;
      }
    }

    return false;
  }

  public async getCurrentPosition(options: { silent?: boolean; updateState?: boolean } = {}): Promise<Position | GeolocationPosition> {
    const { updateState = true } = options;
    const position = this.platformService.isDevice
      ? await this.getCurrentPositionWithDevice(options)
      : await this.getCurrentPositionWithBrowser(options);
    if (updateState) {
      this.updateFromPosition(position, StoreUserLocationRequestSource.Gps);
    }
    return position;
  }

  private async getCurrentPositionWithDevice(options: { silent?: boolean } = {}): Promise<Position> {
    const { silent = false } = options;
    try {
      if (silent && !(await this.hasActiveLocationPermission())) {
        throw new Error('LOCATION_PERMISSION_DENIED');
      }

      const position: Position = await Geolocation.getCurrentPosition(this.geolocationOptions);
      await this.refreshPermissionSnapshot().catch(() => {});
      return position;
    } catch (error) {
      if (!silent) {
        console.error('[Geo] getCurrentPositionWithDevice error', error);
      }
      throw error;
    }
  }

  private async getCurrentPositionWithBrowser(options: { silent?: boolean } = {}): Promise<GeolocationPosition> {
    const { silent = false } = options;
    if (typeof navigator === 'undefined') throw new Error('Geolocation not available');

    try {
      if (silent && 'permissions' in navigator) {
        const permissionsApi: Permissions = (navigator as Navigator & { permissions: Permissions }).permissions;
        const status: PermissionStatus = await permissionsApi.query({ name: 'geolocation' });
        if (status.state !== 'granted') throw new Error('LOCATION_PERMISSION_DENIED');
      }

      const position: GeolocationPosition = await new Promise<GeolocationPosition>((resolve, reject) => {
        const timeoutId = setTimeout(() => reject(new Error('Geolocation request timed out')), this.geolocationOptions.timeout);

        navigator.geolocation.getCurrentPosition(
          (pos: GeolocationPosition) => {
            clearTimeout(timeoutId);
            resolve(pos);
          },
          (error: GeolocationPositionError) => {
            clearTimeout(timeoutId);
            reject(error);
          },
          this.geolocationOptions
        );
      });

      await this.refreshPermissionSnapshot().catch(() => {});
      return position;
    } catch (error) {
      if (!silent) {
        console.warn('[Geo] getCurrentPositionWithBrowser error', error);
      }
      throw error;
    }
  }

  public watchPosition(intervalMs: number = UPDATE_INTERVAL.MY_GPS): Observable<Position | GeolocationPosition> {
    return timer(0, intervalMs).pipe(
      exhaustMap(() =>
        from(this.getCurrentPosition({ silent: true, updateState: false })).pipe(
          tap((pos) => this.registerImprovement(this.positionToResult(pos))),
          catchError((err) => {
            console.warn('[Geo] watchPosition tick error', err);
            // Не завершаем поток, чтобы следующая попытка произошла на следующем тике
            return EMPTY;
          })
        )
      ),
      catchError((err) => {
        console.warn('[Geo] watchPosition stream error', err);
        return EMPTY;
      })
    );
  }

  private applyCoordinate(
    latitude: number,
    longitude: number,
    source: CoordinateUpdateSource,
    options: { persist?: boolean; isFromCache?: boolean; forceDowngrade?: boolean } = {}
  ): void {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

    const { persist = true, isFromCache = false, forceDowngrade = false } = options;
    const precisionRank = this.getCoordinatePrecisionRank(source);

    if (!forceDowngrade && precisionRank < this.coordinatePrecisionRank) {
      return;
    }

    this.currentLatValue = latitude;
    this.currentLonValue = longitude;
    this.hasValidCoordinatesFlag = true;

    if (persist && source !== 'fallback') {
      this.persistCache(latitude, longitude, source);
    }

    this.coordinatePrecisionRank = precisionRank;

    this.coordinateSnapshotSubject.next({
      lat: latitude,
      lon: longitude,
      source,
      timestamp: Date.now(),
      precisionRank,
      isFromCache,
    });
  }

  private applyLocationResult(result: LocationResult): CoordinatePair {
    const fallbackToCache = (): CoordinatePair => {
      if (!this.useCachedCoordinate()) {
        this.useFallbackCoordinates();
      }
      const stored = this.getStoredCoordinates();
      if (!stored) {
        throw new Error('Coordinates are not available');
      }
      return stored;
    };

    switch (result.kind) {
      case 'gps':
      case 'ip':
      case 'cache':
      case 'fallback': {
        const lat = result.coords?.latitude;
        const lon = result.coords?.longitude;
        const resolvedSource: CoordinateUpdateSource = result.source ?? StoreUserLocationRequestSource.Gps;
        if (typeof lat === 'number' && typeof lon === 'number') {
          this.applyCoordinate(lat, lon, resolvedSource, {
            persist: resolvedSource !== 'fallback',
            isFromCache: result.kind === 'cache',
          });
          return { latitude: lat, longitude: lon };
        }
        return fallbackToCache();
      }
      case 'offline':
      case 'blocked':
      default:
        return fallbackToCache();
    }
  }

  private getCoordinatePrecisionRank(source: CoordinateUpdateSource): number {
    switch (source) {
      case StoreUserLocationRequestSource.Ip:
        return 1;
      case StoreUserLocationRequestSource.Gps:
        return 2;
      case 'fallback':
      default:
        return 0;
    }
  }

  private restoreCachedCoordinates(): void {
    if (this.useCachedCoordinate()) return;
    this.useFallbackCoordinates();
  }

  private useCachedCoordinate(): boolean {
    const cached = this.getCachedCoordinate();
    if (!cached) return false;

    this.applyCoordinate(cached.latitude, cached.longitude, cached.source, {
      persist: false,
      isFromCache: true,
    });

    return true;
  }

  public useFallbackCoordinates(): void {
    this.applyCoordinate(DEFAULT_AVATAR_COORDINATE.lat, DEFAULT_AVATAR_COORDINATE.lon, 'fallback', {
      persist: false,
      forceDowngrade: this.coordinatePrecisionRank < 0,
    });
  }

  private buildPseudoPosition(latitude: number, longitude: number): Position {
    const position: Position = {
      coords: {
        latitude,
        longitude,
        accuracy: Number.NaN,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        speed: null,
        magneticHeading: null,
        trueHeading: null,
        headingAccuracy: null,
        course: null
      },
      timestamp: Date.now()
    };
    return position;
  }

  // Стриминговые обновления координат (для продюсера)
  public startVideoLocationUpdates(videoId: number): void {
    this.stopVideoLocationUpdates();
    this.currentVideoId = videoId;
    this.prevVideoLat = null;
    this.prevVideoLon = null;
    this.videoGpsSent = false;
    this.videoIpSent = false;
    this.videoLocationSending = false;
    this.resetPrecisionTracking();

    this.videoIpFallbackTimer = setTimeout(() => {
      if (!this.videoGpsSent && this.currentVideoId) {
        this.triggerVideoIpFallback();
      }
      this.videoIpFallbackTimer = null;
    }, this.videoIpFallbackMs);

    this.videoTrackSub = this.watchPosition(UPDATE_INTERVAL.MY_GPS).subscribe({
      next: (pos) => this.sendVideoLocation(pos.coords.latitude, pos.coords.longitude, this.videoLocationSource.Gps),
      error: () => {},
    });

    this.videoImproveSub = this.improvedCoordinate$
      .pipe(debounceTime(500))
      .subscribe(({ lat, lon, source }) => {
        if (source === StoreUserLocationRequestSource.Gps) {
          this.sendVideoLocation(lat, lon, this.videoLocationSource.Gps).catch(() => {});
        }
      });
  }

  public stopVideoLocationUpdates(): void {
    if (this.videoTrackSub) {
      this.videoTrackSub.unsubscribe();
      this.videoTrackSub = null;
    }
    if (this.videoImproveSub) {
      this.videoImproveSub.unsubscribe();
      this.videoImproveSub = null;
    }
    if (this.videoIpFallbackSub) {
      this.videoIpFallbackSub.unsubscribe();
      this.videoIpFallbackSub = null;
    }
    if (this.videoIpFallbackTimer) {
      clearTimeout(this.videoIpFallbackTimer);
      this.videoIpFallbackTimer = null;
    }
    this.currentVideoId = null;
    this.prevVideoLat = null;
    this.prevVideoLon = null;
    this.videoGpsSent = false;
    this.videoIpSent = false;
    this.videoLocationSending = false;
  }

  private async sendVideoLocation(
    lat?: number,
    lon?: number,
    source: UpdateVideoLocationRequestLocation_source = this.videoLocationSource.Gps
  ): Promise<void> {
    if (!this.currentVideoId) return;
    if (this.videoGpsSent || this.videoIpSent) return;
    if (this.videoLocationSending) return;
    if (source === this.videoLocationSource.Ip) {
      if (this.videoGpsSent || this.videoIpSent) return;
    }
    if (lat != null && lon != null && lat === this.prevVideoLat && lon === this.prevVideoLon && source === this.videoLocationSource.Gps) {
      console.log('[Geo] video location unchanged (producer)', { videoId: this.currentVideoId, lat, lon });
      return;
    }
    this.videoLocationSending = true;
    try {
      console.log('[Geo] send video location (producer)', { videoId: this.currentVideoId, lat, lon, source });
      const body = new UpdateVideoLocationRequest();
      body.location_source = source;
      if (source === this.videoLocationSource.Ip) {
        body.accuracy = 'ip';
      }
      if (lat != null && lon != null) {
        body.long = lon;
        body.lat = lat;
      }
      await firstValueFrom(this.client.videosUpdateLocation(this.currentVideoId, body));
      if (lat != null && lon != null) {
        this.prevVideoLat = lat;
        this.prevVideoLon = lon;
      }
      if (source === this.videoLocationSource.Gps) {
        this.videoGpsSent = true;
        if (this.videoIpFallbackSub) {
          this.videoIpFallbackSub.unsubscribe();
          this.videoIpFallbackSub = null;
        }
        if (this.videoIpFallbackTimer) {
          clearTimeout(this.videoIpFallbackTimer);
          this.videoIpFallbackTimer = null;
        }
      } else if (source === this.videoLocationSource.Ip) {
        this.videoIpSent = true;
      }
    } catch (err) {
      console.warn('[Geo] Send video location failed', err);
    } finally {
      this.videoLocationSending = false;
    }
  }

  private triggerVideoIpFallback(): void {
    if (!this.currentVideoId || this.videoGpsSent || this.videoIpSent) return;

    if (this.onlineStateService.isOffline) {
      if (this.videoIpFallbackSub) return;
      this.videoIpFallbackSub = this.onlineStateService.isOffline$
        .pipe(
          filter((isOffline: boolean): boolean => !isOffline),
          first()
        )
        .subscribe(() => {
          if (this.videoIpFallbackSub) {
            this.videoIpFallbackSub.unsubscribe();
            this.videoIpFallbackSub = null;
          }
          void this.sendVideoLocation(undefined, undefined, this.videoLocationSource.Ip);
        });
      return;
    }

    void this.sendVideoLocation(undefined, undefined, this.videoLocationSource.Ip);
  }

  private positionToResult(pos: Position | GeolocationPosition): LocationResult {
    return {
      kind: 'gps',
      coords: {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
      },
      source: StoreUserLocationRequestSource.Gps,
    };
  }
  //#endregion GPS

  //#region IP
  private async tryIp(): Promise<LocationResult | null> {
    if (this.onlineStateService.isOffline) {
      return { kind: 'offline', reason: 'offline' };
    }

    try {
      const locationByIP: PositionResponse = await firstValueFrom(this.client.coordinateGetPosition());
      if (locationByIP?.latitude == null || locationByIP?.longitude == null) {
        return null;
      }

      const coords = { latitude: locationByIP.latitude, longitude: locationByIP.longitude };
      this.persistCache(coords.latitude, coords.longitude, StoreUserLocationRequestSource.Ip);
      return { kind: 'ip', coords, source: StoreUserLocationRequestSource.Ip };
    } catch (_err) {
      return null;
    }
  }
  //#endregion IP

  //#region Cache
  private getCachedCoordinate(): (CachedCoordinatePayload & { latitude: number; longitude: number }) | null {
    if (!this.cacheEnabled) return null;

    const payload = this.localStorage.getItem(this.cacheKey, true) as CachedCoordinatePayload | null;
    if (!payload) return null;
    if (typeof payload.lat !== 'number' || typeof payload.lon !== 'number') return null;
    if (!Object.values(StoreUserLocationRequestSource).includes(payload.source)) return null;
    if (!this.isCacheFresh(payload.timestamp)) return null;

    return {
      ...payload,
      latitude: payload.lat,
      longitude: payload.lon,
    };
  }

  private isCacheFresh(timestamp?: number): boolean {
    if (!timestamp) return false;
    return (Date.now() - timestamp) <= this.cacheTtlMs;
  }

  private persistCache(latitude: number, longitude: number, source: StoreUserLocationRequestSource): void {
    if (!this.cacheEnabled) return;

    const payload: CachedCoordinatePayload = {
      lat: latitude,
      lon: longitude,
      source,
      timestamp: Date.now(),
    };
    this.localStorage.setItem(this.cacheKey, payload);
  }
  //#endregion Cache

  private registerImprovement(result: LocationResult): void {
    if (!result?.coords) return;

    const rank = this.precisionRank[result.kind] ?? 0;
    if (rank > this.lastPrecisionRank) {
      this.lastPrecisionRank = rank;
      const resolvedSource: StoreUserLocationRequestSource | 'fallback' = result.source ?? 'fallback';
      console.log('[Geo] precision improved', {
        kind: result.kind,
        lat: result.coords.latitude,
        lon: result.coords.longitude,
        source: resolvedSource,
        rank,
      });
      this.improvedCoordinateSubject.next({
        lat: result.coords.latitude,
        lon: result.coords.longitude,
        source: resolvedSource,
        precisionRank: rank,
      });
    }
  }

  //#region Permission snapshot
  public async refreshPermissionSnapshot(): Promise<PermissionStatusNormalized> {
    const status = await this.getPermissionStatus();
    this.localStorage.setItem(this.gpsPermissionSnapshotKey, status);
    return status;
  }

  public async requestPermissions(): Promise<PermissionStatusNormalized> {
    if (!this.platformService.isDevice) {
      // В браузере нет отдельного запроса, статус изменится при getCurrentPosition
      return this.refreshPermissionSnapshot();
    }

    try {
      await Geolocation.requestPermissions();
    } catch (error) {
      console.warn('[Geo] requestPermissions error', error);
    }

    return this.refreshPermissionSnapshot();
  }

  public getPermissionSnapshot(): PermissionStatusNormalized | null {
    return this.localStorage.getItem(this.gpsPermissionSnapshotKey, true) as PermissionStatusNormalized | null;
  }
  //#endregion Permission snapshot

  //#region High-level helpers
  public async acquireBackground(): Promise<LocationResult> {
    return this.acquire('background', {
      allowPrompt: false,
      policy: this.gpsPolicy,
    });
  }

  public async acquireForCenter(): Promise<LocationResult> {
    return this.acquireInteractive();
  }

  public async acquireExplicit(): Promise<LocationResult> {
    return this.acquireInteractive();
  }
  //#endregion High-level helpers

  // Главный интерактивный путь для явных действий (стрелка, старт стрима)
  public async acquireInteractive(): Promise<LocationResult> {
    const policy = this.gpsPolicy;

    // Всегда обновляем снимок перед явным запросом
    const status = await this.refreshPermissionSnapshot();
    const { enabled, permission } = status;

    const shouldForcePrompt = policy === 'ask' || permission !== 'granted';

    console.log('[Geo] acquireInteractive', { policy, permission, enabled, shouldForcePrompt });

    // Гео выключено системно — тихо уходим в IP/fallback
    if (enabled === false) {
      console.log('[Geo] system location disabled -> background/IP');
      return this.acquire('background', { allowPrompt: false, policy });
    }

    // Политика deny — не трогаем GPS
    if (policy === 'deny') {
      console.log('[Geo] policy deny -> background/IP');
      return this.acquire('background', { allowPrompt: false, policy });
    }

    // Явное действие: всегда интерактивно. ОС сама решит, показать ли диалог.
    console.log('[Geo] interactive request', { permission, force: shouldForcePrompt });
    return this.acquire('interactive', { allowPrompt: true, policy, forcePrompt: shouldForcePrompt });
  }
}
